import Color from 'color';

import { APP_COLORS, STAGE_COLORS } from '../constants/color';

/**
 * Converts any color string to RGBA CSS format with custom alpha
 * @param color - Color string (hex, rgb, hsl, named color, etc.)
 * @param alpha - Alpha value (0-1)
 * @returns RGBA CSS format string (e.g., "rgba(255, 0, 0, 0.5)")
 */
export function toRgba(color: string, alpha: number): string | null {
	try {
		if (alpha < 0 || alpha > 1) {
			console.error(`Alpha value must be between 0 and 1, got: ${alpha}`);
			return null;
		}

		const colorObj = Color(color);
		const rgb = colorObj.rgb().object();

		return `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${alpha})`;
	} catch (error) {
		console.error(`Invalid color: ${color}`);
		return null;
	}
}

/**
 * Generates a deterministic, visually distinct color based on text input.
 * Uses HSL color space to ensure good contrast and visibility.
 *
 * @param text - Input string to generate color from
 * @param checkForStage - Whether to check for predefined stage colors first
 * @returns Hex color string
 */
export function getRandomColorFromText(text: string, checkForStage: boolean = false): string {
	if (checkForStage) {
		const stageColor = STAGE_COLORS[text.toLowerCase() as keyof typeof STAGE_COLORS];
		if (stageColor) return stageColor;
	}

	// Generate a deterministic hash from the text
	const hash = Math.abs(
		text.split('').reduce((acc, char) => {
			return char.charCodeAt(0) + ((acc << 5) - acc);
		}, 0)
	);

	try {
		// Generate base color with full hue range and high saturation
		const hue = hash % 360;
		const saturation = 70 + (hash % 20); // 70-90% for vibrant colors

		// Start with a base lightness
		let color = Color.hsl(hue, saturation, 50);

		// Calculate relative luminance (WCAG standard)
		const luminance = color.luminosity();

		// Adjust lightness based on luminance for better text visibility
		// Target: darker colors for light backgrounds, lighter for dark backgrounds
		if (luminance > 0.5) {
			// Too light - darken it
			color = color.lightness(30 + (hash % 15)); // 30-45%
		} else if (luminance < 0.18) {
			// Too dark - lighten it
			color = color.lightness(65 + (hash % 15)); // 65-80%
		}
		// else: luminance is in a good range (0.18-0.5), keep it

		return color.hex();
	} catch {
		// Fallback to a safe default
		return '#6366f1';
	}
}

/**
 * Generates a deterministic color based on text input.
 * Uses APP_COLORS as base colors and applies shade variations
 * based on the hash to create more unique colors while staying cohesive.
 *
 * @param text - Input string to generate color from
 * @param checkForStage - Whether to check for predefined stage colors first
 * @returns Hex color string
 */
export function getRandomAppColorByText(text: string, checkForStage: boolean = false): string {
	if (checkForStage) {
		const stageColor = STAGE_COLORS[text.toLowerCase() as keyof typeof STAGE_COLORS];
		if (stageColor) return stageColor;
	}

	// Generate a deterministic hash from the text
	const hash = Math.abs(
		text.split('').reduce((acc, char) => {
			return char.charCodeAt(0) + ((acc << 5) - acc);
		}, 0)
	);

	// Use different parts of the hash for color selection and shade variation
	const colors = Object.keys(APP_COLORS).filter(
		(key) => key !== 'WHITE' && key !== 'GREY' // Exclude neutral colors for better variety
	);
	const colorIdx = hash % colors.length;
	const baseColor = APP_COLORS[colors[colorIdx] as keyof typeof APP_COLORS];

	// Use a different part of the hash to determine shade variation
	// This creates 7 possible variations: -15%, -10%, -5%, 0%, +5%, +10%, +15% lightness
	const shadeVariations = [-0.15, -0.1, -0.05, 0, 0.05, 0.1, 0.15];
	const shadeIdx = Math.floor(hash / colors.length) % shadeVariations.length;
	const lightnessAdjustment = shadeVariations[shadeIdx];

	try {
		const colorObj = Color(baseColor);
		const adjustedColor = colorObj.lightness(colorObj.lightness() + lightnessAdjustment * 100);
		return adjustedColor.hex();
	} catch {
		return baseColor;
	}
}
